/**
 * orgchart domain — reporting relationships, delegated coverage, and the
 * authority a person actually holds right now.
 *
 * ---------------------------------------------------------------------------
 * `managers` IS AN ARRAY, AND THAT IS THE WHOLE POINT
 * ---------------------------------------------------------------------------
 * The API returns every manager a person has on a given date, because a person
 * genuinely has more than one: an ORGANIZATIONAL MANAGER at org scope and a
 * PROGRAM SUPERVISOR at program scope coexist, and neither is "the" manager.
 * A single-manager signature on this side — `manager: Person | null`, or a
 * `parentId` on a node — would quietly reintroduce the tree the data model
 * refuses, and the first thing it would do is drop somebody's real supervisor.
 *
 * So this module has no `manager()` accessor, no `primaryManager`, and no
 * function that reduces the array. {@link managersByScope} groups them so a
 * screen can SHOW BOTH; it never picks one.
 *
 * ---------------------------------------------------------------------------
 * DELEGATED AUTHORITY IS NEVER WRITTEN DOWN
 * ---------------------------------------------------------------------------
 * {@link effectiveAuthority} is re-resolved by the server on every call from
 * `starts_at <= now() < ends_at`. There is no materialised grant to revoke and
 * nothing to clean up: the same request one second after a coverage window
 * closes returns an empty `delegated`. A surface must therefore RE-ASK rather
 * than cache, and must show the window's end — "until when" is half the fact.
 *
 * ---------------------------------------------------------------------------
 * PEOPLE, PHOTOS AND NAMES
 * ---------------------------------------------------------------------------
 * Every person in these payloads is an `app_user.id` and nothing else. No node
 * carries a name, and none carries an avatar field — per
 * `docs/PROFILE-PHOTO-CONTRACT.md`, "a node is a person; it carries `userId`
 * and the version, nothing else". Names and photo versions are resolved by the
 * surface against `directory.workers()`, which already carries `userId`,
 * `displayName` and `photoUpdatedAt`.
 */
import { del, get, post, put } from './transport.js';
/** Human-readable labels. `custom` defers to the customer's own type name. */
export const RELATIONSHIP_KIND_LABEL = Object.freeze({
    supervisor: 'Supervisor',
    organizational_manager: 'Organizational manager',
    program_supervisor: 'Program supervisor',
    program_manager: 'Program manager',
    director: 'Director',
    administrator: 'Administrator',
    custom: 'Custom relationship',
});
export const SCOPE_TYPE_LABEL = Object.freeze({
    org: 'Organization-wide',
    program: 'Program',
    location: 'Location',
    team: 'Team',
});
/**
 * `GET /orgchart/users/:id` → the reporting line (needs `orgchart:read`).
 * `asOf` is a date (YYYY-MM-DD); omitted means today.
 */
export async function reportingLine(userId, asOf, options) {
    const body = await get(`/orgchart/users/${userId}`, {
        ...options,
        query: asOf ? { asOf } : undefined,
    });
    return body.reportingLine;
}
/** `GET /orgchart/users/:id/history` → every relationship ever recorded. */
export async function relationshipHistory(userId, options) {
    const body = await get(`/orgchart/users/${userId}/history`, options);
    return body.relationships;
}
const SCOPE_ORDER = Object.freeze([
    'org',
    'program',
    'location',
    'team',
]);
export function managersByScope(line) {
    const groups = [];
    for (const scopeType of SCOPE_ORDER) {
        const managers = line.managers.filter((m) => m.scopeType === scopeType);
        if (managers.length === 0)
            continue;
        groups.push({ scopeType, label: SCOPE_TYPE_LABEL[scopeType], managers });
    }
    return groups;
}
/** `POST /orgchart/relationships` → 201 `{ relationship }` (`orgchart:manage`). */
export async function createRelationship(input, options) {
    const body = await post('/orgchart/relationships', input, options);
    return body.relationship;
}
/** `POST /orgchart/relationships/:id/end` → `{ relationship }`. */
export async function endRelationship(id, effectiveTo, options) {
    const body = await post(`/orgchart/relationships/${id}/end`, { effectiveTo }, options);
    return body.relationship;
}
/**
 * `GET /orgchart/relationship-types` → the customer's own relationship types,
 * with the wire's column names mapped to this package's vocabulary.
 */
export async function relationshipTypes(options) {
    const body = await get('/orgchart/relationship-types', options);
    return body.relationshipTypes.map((row) => ({
        id: row.id,
        key: row.key,
        name: row.name,
        description: row.description,
        authorityBearing: row.authority_bearing,
        retiredAt: row.retired_at,
    }));
}
/** `POST /orgchart/relationship-types` → 201 `{ relationshipType }` (`orgchart:manage`). */
export async function createRelationshipType(input, options) {
    const body = await post('/orgchart/relationship-types', input, options);
    const row = body.relationshipType;
    return {
        id: row.id,
        key: row.key,
        name: row.name,
        description: row.description,
        authorityBearing: row.authority_bearing,
        retiredAt: row.retired_at,
    };
}
/**
 * `GET /orgchart/teams` → `{ teams }` (`orgchart:read`).
 *
 * The read side of team scope. Before it existed the relationship editor asked
 * an administrator to TYPE a team uuid and a team-scoped edge rendered that
 * uuid at the reader; a scope whose referents cannot be enumerated is a scope
 * nobody can use correctly.
 *
 * `[]` is an organization with no teams — a real measured zero, not a refusal.
 * A caller without `orgchart:read` gets a 403 and this function throws.
 */
export async function teams(options) {
    const body = await get('/orgchart/teams', options);
    return body.teams;
}
/**
 * `POST /orgchart/teams` → 201 `{ team }` (`orgchart:manage`).
 *
 * A team is one of the four scopes a relationship can be held at
 * (`RelationshipScopeType`), so creating one widens the vocabulary the chart
 * can express. Read them back with {@link teams}.
 */
export async function createTeam(input, options) {
    const body = await post('/orgchart/teams', input, options);
    return body.team;
}
/**
 * `GET /orgchart/superusers` → the designated tier and its cap.
 *
 * Designation is deliberately not gated on a permission key: if it were,
 * `role:assign` could hand that key out and the tier would be reachable from
 * the permission system after all.
 */
export async function superusers(options) {
    return get('/orgchart/superusers', options);
}
/** `POST /orgchart/superusers` → 201 `{ designation }`. */
export async function designateSuperuser(input, options) {
    const body = await post('/orgchart/superusers', input, options);
    return body.designation;
}
/** `DELETE /orgchart/superusers/:userId` → 204. */
export function revokeSuperuser(userId, options) {
    return del(`/orgchart/superusers/${userId}`, options);
}
/** `POST /orgchart/absences` → 201 `{ absence }` (`orgchart:delegate`). */
export async function recordAbsence(input, options) {
    const body = await post('/orgchart/absences', input, options);
    return body.absence;
}
/** `POST /orgchart/coverage/evaluate` → `{ assessment }`. */
export async function evaluateCoverage(input, options) {
    const body = await post('/orgchart/coverage/evaluate', input, options);
    return body.assessment;
}
/**
 * `POST /orgchart/coverage` → 201 `{ coverage }` with the inherited keys.
 *
 * `inheritedKeys` is the SERVER'S statement of what was actually lent, capped at
 * what the covered person held when the ceiling ran. It is the only trustworthy
 * answer to "what did this delegation grant"; anything a screen computes before
 * the press is a preview of a live figure, not the grant.
 */
export async function assignCoverage(input, options) {
    const body = await post('/orgchart/coverage', input, options);
    return body.coverage;
}
/** `POST /orgchart/coverage/:id/revoke` → 204. */
export function revokeCoverage(id, reason, options) {
    return post(`/orgchart/coverage/${id}/revoke`, reason ? { reason } : {}, options);
}
/**
 * `GET /orgchart/users/:id/effective-authority` → what this person holds RIGHT
 * NOW, own plus borrowed.
 *
 * Do not cache the result across a window boundary: it is a statement about an
 * instant, and the instant is in the payload (`at`). A screen holding a
 * delegated view asks again on its next request and is told no.
 */
export async function effectiveAuthority(userId, options) {
    const body = await get(`/orgchart/users/${userId}/effective-authority`, options);
    return body.authority;
}
/** `POST /orgchart/roles` → 201 `{ role }` (`role:compose`). */
export async function composeRole(input, options) {
    const body = await post('/orgchart/roles', input, options);
    return body.role;
}
/** `PUT /orgchart/roles/:key` → `{ role }`. */
export async function recomposeRole(key, permissions, options) {
    const body = await put(`/orgchart/roles/${encodeURIComponent(key)}`, { permissions }, options);
    return body.role;
}
/**
 * `POST /orgchart/roles/assign` → 201 `{ granted }`.
 *
 * Needs BOTH `role:compose` and `role:assign`: putting authority onto a person
 * takes the role-granting key, whichever surface it arrives through.
 */
export async function assignComposedRole(input, options) {
    const body = await post('/orgchart/roles/assign', input, options);
    return body.granted;
}
/** `GET /orgchart/authority-history` — every change to who may do what. */
export async function authorityHistory(query = {}, options) {
    const body = await get('/orgchart/authority-history', {
        ...options,
        query: { ...query },
    });
    return body.events;
}
//# sourceMappingURL=orgchart.js.map